/**
 * Shared Flight School analytics contract.
 *
 * Keep this in sync with the available Flight School modules so PostHog
 * funnels, milestone events, and graduation cohorts stay comparable as the
 * course expands beyond cognates.
 */
export const FLIGHT_SCHOOL_VERSION = 2;

export interface FlightSchoolMilestones {
  one_third: number;
  halfway: number;
  two_thirds: number;
}

export interface FlightSchoolModuleAnalytics {
  id: string;
  eventPrefix: string;
  totalActivities: number;
  milestones: FlightSchoolMilestones;
}

export const FLIGHT_SCHOOL_MODULE_ANALYTICS: FlightSchoolModuleAnalytics[] = [
  {
    id: "cognates-1",
    eventPrefix: "cognates_1",
    totalActivities: 20,
    milestones: { one_third: 7, halfway: 10, two_thirds: 14 },
  },
  {
    id: "cognates-2",
    eventPrefix: "cognates_2",
    totalActivities: 19,
    milestones: { one_third: 7, halfway: 10, two_thirds: 14 },
  },
  {
    id: "cognates-3",
    eventPrefix: "cognates_review",
    totalActivities: 18,
    milestones: { one_third: 5, halfway: 8, two_thirds: 10 },
  },
  {
    id: "basic-phrases-1",
    eventPrefix: "basic_phrases_1",
    totalActivities: 26,
    milestones: { one_third: 9, halfway: 13, two_thirds: 18 },
  },
  {
    id: "basic-phrases-2",
    eventPrefix: "basic_phrases_2",
    totalActivities: 31,
    milestones: { one_third: 10, halfway: 15, two_thirds: 21 },
  },
];

const MODULE_ANALYTICS_BY_ID = FLIGHT_SCHOOL_MODULE_ANALYTICS.reduce<
  Record<string, FlightSchoolModuleAnalytics>
>((byId, module) => {
  byId[module.id] = module;
  return byId;
}, {});

export function getFlightSchoolModuleAnalytics(
  moduleId: string,
): FlightSchoolModuleAnalytics | undefined {
  return MODULE_ANALYTICS_BY_ID[moduleId];
}

export function getFlightSchoolEventPrefix(moduleId: string): string {
  return getFlightSchoolModuleAnalytics(moduleId)?.eventPrefix ?? moduleId;
}

export function getFlightSchoolMilestones(
  moduleId: string,
): FlightSchoolMilestones | undefined {
  return getFlightSchoolModuleAnalytics(moduleId)?.milestones;
}

export function getFlightSchoolModuleEventProps(moduleId: string) {
  const moduleAnalytics = getFlightSchoolModuleAnalytics(moduleId);

  return {
    module_id: moduleId,
    module_event_prefix: getFlightSchoolEventPrefix(moduleId),
    module_total_activities: moduleAnalytics?.totalActivities ?? null,
    flight_school_version: FLIGHT_SCHOOL_VERSION,
  };
}

export function getFlightSchoolModuleEventName(
  moduleId: string,
  suffix: string,
): string {
  return `flight_school_${getFlightSchoolEventPrefix(moduleId)}_${suffix}`;
}

export function getFlightSchoolActivityStepEventName(
  moduleId: string,
  activityIndex: number,
): string {
  return getFlightSchoolModuleEventName(
    moduleId,
    `step_${activityIndex + 1}_completed`,
  );
}
